import DeviceApi from '@api/DeviceApi';
import { TagProfile } from '@constants/Tag';
import { faArrowRight, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { DeviceModel } from '@models/Device';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Switch } from 'react-native-switch';

import LoadingSpinner from '../../../components/Loader/Loader';
import { SpProfileImage } from '../../../components/SpProfileImage';
import { SpText } from '../../../components/SpText/SpText';
import { SpView } from '../../../components/SpView';
import useBoundStore from '../../../store/store';
import atomStyles from '../../../styles/atoms';
import colors from '../../../styles/colors';
import noop from '../../../utils/noop';
import { testProperties } from '../../../utils/testProperties';

type IndoorModePetItemType = {
  imageUrl: string;
  assigned?: boolean;
  indoorModeEnabled?: boolean;
  name: string;
  created?: string;
  withAssignedIcon?: boolean;
  handlePress?: (profile: TagProfile) => void;
  isIndoorMode?: boolean;
  isAlternativeStyle?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isIncompatible?: boolean;
  id?: number;
  device: DeviceModel;
};

const IndoorModePetItem = ({
  imageUrl,
  assigned,
  indoorModeEnabled,
  name,
  id,
  handlePress,
  created = null,
  withAssignedIcon = false,
  isIndoorMode = false,
  isAlternativeStyle = false,
  isDisabled = false,
  isLoading = false,
  isIncompatible = false,
  device,
}: IndoorModePetItemType) => {
  const { t } = useTranslation();
  const { loadingControl, setLoadingControl, updateDevice } = useBoundStore(s => s.deviceStore);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [refreshingTags, setRefreshingTags] = useState(false);
  useEffect(() => {
    console.log('isLoadingTags', isLoadingTags);
    if (isLoadingTags && !loadingControl.tag_profiles?.some(item => item.tag_id === id)) {
      console.log('LOADING TAG FINISHED, UPDATE DEVICE');
      setRefreshingTags(true);
      DeviceApi.getDeviceTags(device.id).then(res => {
        console.log(res);
        console.log(device);
        const newDevice = { ...device, tags: res };
        updateDevice(newDevice);
        setRefreshingTags(false);
      });
    }
  }, [isLoadingTags, loadingControl]);

  useEffect(() => {
    // console.log('IndoorWrapper loadingControl', loadingControl);

    // When loading control goes from having a property tag_profiles with a posative lenght to being empty or tag_profiles with 0 length, reload device tags
    setIsLoadingTags(
      loadingControl.tag_profiles?.length > 0 &&
        loadingControl.tag_profiles.some(item => item.tag_id === id),
    );
  }, [loadingControl]);

  const check = useMemo(() => {
    let result = true;
    if (loadingControl.tag_profiles) {
      loadingControl.tag_profiles.forEach(item => (item.tag_id === id ? (result = false) : null));
    }
    return result;
  }, [loadingControl, id]);

  const onValueChange = useCallback(
    value => {
      if (!check) {
        return console.log('Indoor mode changing in progress');
      }
      if (typeof value === 'boolean') {
        return handlePress(value ? TagProfile.Safe : TagProfile.Permitted);
      } else {
        return handlePress(!indoorModeEnabled ? TagProfile.Safe : TagProfile.Permitted);
      }
    },
    [loadingControl?.tag_profiles, check],
  );

  return isIncompatible ? (
    <Pressable
      accessible={false}
      style={[styles.container, styles.isLoading]}
      onPress={onValueChange}>
      <SpView style={styles.petItem}>
        <SpView
          marginRight={16}
          {...testProperties(`${name}`, `PetImage`)}>
          <SpProfileImage
            width={48}
            height={48}
            imageUrl={imageUrl}
          />
        </SpView>
        <SpView style={styles.petDetails}>
          <SpText style={styles.name}>{name}</SpText>
          <SpView>
            <SpText style={styles.time}>{t('incompatible_pet')}</SpText>
          </SpView>
        </SpView>
      </SpView>
      <View style={!assigned ? atomStyles.alternativeCheckbox : null}>
        <FontAwesomeIcon
          size={32}
          icon={faArrowRight}
          color={colors.greyDisabled.color}
        />
      </View>
    </Pressable>
  ) : (
    <Pressable
      accessible={false}
      style={[
        styles.container,
        isLoading ? styles.isLoading : {},
        isDisabled ? styles.isLoading : {},
        isIncompatible ? styles.isLoading : {},
      ]}
      onPress={isDisabled ? noop : onValueChange}>
      <SpView style={styles.petItem}>
        <SpView
          marginRight={16}
          {...testProperties(`${name}`, `PetImage`)}>
          <SpProfileImage
            width={48}
            height={48}
            imageUrl={imageUrl}
          />
        </SpView>

        {indoorModeEnabled && (!loadingControl.tag_profiles || check) && !refreshingTags ? (
          <>
            <SpView style={styles.petDetails}>
              <SpText style={styles.name}>{name}</SpText>
              <SpText style={styles.enabled}>{indoorModeEnabled && t('enabled')}</SpText>
            </SpView>
            <SpView>
              <SpText style={styles.time}>{created}</SpText>
            </SpView>
          </>
        ) : (
          <SpView style={styles.petDetails}>
            <SpText style={styles.name}>{name}</SpText>
          </SpView>
        )}
      </SpView>

      {/* {!isIndoorMode &&
        !isAlternativeStyle &&
        (withAssignedIcon ? (
          <View
            {...testProperties(
              assigned ? 'faCheckCircle' : 'faCirclePlus',
              `icon`,
            )}
            style={!assigned ? atomStyles.alternativeCheckbox : null}
          >
            <FontAwesomeIcon
              size={32}
              icon={assigned ? faCheckCircle : faCircle}
              color={assigned ? colors.primary.color : colors.white.color}
            />
          </View>
        ) : (
          <View {...testProperties('faAngleRight', 'icon')}>
            <FontAwesomeIcon
              size={20}
              icon={faAngleRight}
              color={colors.greyText.color}
            />
          </View>
        ))} */}

      {isAlternativeStyle && (
        <SpView
          {...testProperties('faCheckCircle', `icon_${name || ''}`)}
          style={atomStyles.alternativeCheckbox}>
          {isLoading && <LoadingSpinner size="small" />}
          {assigned && !isLoading && (
            <FontAwesomeIcon
              size={32}
              icon={faCheckCircle}
              color={colors.primary.color}
            />
          )}
        </SpView>
      )}
      {isIndoorMode && (!loadingControl.tag_profiles || check) && !refreshingTags ? (
        <Switch
          value={indoorModeEnabled}
          onValueChange={onValueChange}
          disabled={false}
          renderActiveText={false}
          renderInActiveText={false}
          backgroundActive={colors.green.color}
          backgroundInactive={colors.greyBorder.color}
          circleSize={25}
          circleBorderWidth={0}
          containerStyle={{
            minHeight: 32,
            position: 'absolute',
            right: 18
          }}
          innerCircleStyle={{
            width: 25,
            height: 25,
            backgroundColor: colors.white.color,
          }}
          switchBorderRadius={18}
          switchLeftPx={3}
          switchRightPx={3}
          {...testProperties(`${name} pet`, 'switch')}
        />
      ) : (
        <SpView style={{position: 'absolute', right: 18}}>
          <LoadingSpinner />
        </SpView>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 17,
    borderColor: colors.greyBorder.color,
    borderWidth: 1,
    borderRadius: 14,
    marginBottom: 25,
  },
  petItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  petDetails: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexShrink: 1,
    overflow: 'hidden'
  },
  name: {
    fontFamily: 'Rubik',
    fontSize: 16,
    color: colors.greyText.color,
    paddingRight: 64,
  },
  time: {
    fontWeight: 'bold',
    fontSize: 12,
    display: 'flex',
  },
  enabled: {
    fontFamily: 'Rubik_Medium',
    fontSize: 14,
    color: colors.green.color,
  },
  isLoading: {
    opacity: 0.6,
  },
});

export default IndoorModePetItem;
